import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ApplicationStatus } from '~/enums';
import { removeWhitespace } from '~/helpers/formatters.helper';
import { buildStreetValue } from '~/helpers/shared.helper';
import {
  ICardApplicationApplicant,
  ICardApplicationForm,
  ICardApplicationState,
} from '~/interfaces/cardApplication';
import { IAddressFields } from '~/interfaces/shared';
import { listenerMiddleware } from '../middlewares/listener.middleware';
import { ApiStatus } from '../types/sharedTypes';
import { CardDataPointsShared } from '~/schemas/DTO/cardApplicationSchemas';

export const cardApplicationInitialState = Object.freeze<ICardApplicationState>(
  {
    currentStepNumber: 1,
    currentStepName: undefined,
    stepStartTimestamp: new Date().toString(),
    form: {
      applicantIdentified: false,
      organizationNumber: '',
      sessionId: '',
      applicant: {},
      qCode: undefined,
      purposeOfCard: undefined,
      cardPurposeManual: undefined,
      companyType: undefined,
      companyName: undefined,
      turnover: '',
    },
    apiStatus: {
      get: ApiStatus.Idle,
      post: ApiStatus.Idle,
      patch: ApiStatus.Idle,
      companyLookupData: ApiStatus.Idle,
      companySearch: ApiStatus.Idle,
    },
    applicationStatus: ApplicationStatus.None,
    dataPointsShared: {
      company: false,
      cardPurpose: false,
      cardPurposeReason: false,
      turnover: false,
      applicantEmail: false,
      applicantPhone: false,
      applicantPersonalNumber: false,
      applicantDateOfBirth: false,
      applicantFullName: false,
      applicantAddress: false,
    },
  }
);

const cardApplicationSlice = createSlice({
  name: 'cardApplication',
  initialState: cardApplicationInitialState,
  reducers: {
    updateCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStepNumber = action.payload;
    },
    goToNextStep: (state) => {
      state.currentStepNumber += 1;
    },
    goToPreviousStep: (state) => {
      state.currentStepNumber -= 1;
    },
    setCurrentStepName: (
      state,
      action: PayloadAction<ICardApplicationState['currentStepName']>
    ) => {
      state.currentStepName = action.payload;
    },
    updateForm: (
      state,
      action: PayloadAction<Partial<ICardApplicationForm>>
    ) => {
      state.form = { ...state.form, ...action.payload };
    },
    setUserId: (state, action: PayloadAction<number>) => {
      state.userId = action.payload;
    },
    setClientId: (state, action: PayloadAction<number>) => {
      state.clientId = action.payload;
    },
    setApiStatus: (
      state,
      action: PayloadAction<Partial<ICardApplicationState['apiStatus']>>
    ) => {
      state.apiStatus = {
        ...state.apiStatus,
        ...action.payload,
      };
    },
    setStepStartTimestamp: (state) => {
      state.stepStartTimestamp = new Date().toString();
    },
    updateApplicationStatus: (
      state,
      action: PayloadAction<ApplicationStatus>
    ) => {
      state.applicationStatus = action.payload;
    },
    updateCurrentOrganizationIdentifier: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.currentApplicationOrganizationIdentifier = action.payload || '';
    },
    clearCardApplicationState: () => ({
      ...cardApplicationInitialState,
      cleared: true,
    }),
    removeClearedState: (state) => {
      state.cleared = false;
    },
    updateApplicantData: (
      state,
      action: PayloadAction<ICardApplicationApplicant>
    ) => {
      state.form.applicant = {
        ...state.form.applicant,
        ...action.payload,
      };
    },
    updateApplicantAddress: (state, action: PayloadAction<IAddressFields>) => {
      state.form.applicant.addressFields = {
        ...state.form.applicant.addressFields,
        ...action.payload,
      };

      const defaultStreetValue = state.form.applicant.address?.street ?? '';
      const defaultZipCodeValue = state.form.applicant.address?.zipCode ?? '';
      const defaultCityValue = state.form.applicant.address?.city ?? '';

      state.form.applicant.address = {
        street:
          action.payload.streetName ||
          action.payload.houseNumber ||
          action.payload.houseNumberAddition
            ? buildStreetValue({
                ...state.form.applicant.addressFields,
                ...action.payload,
              })
            : defaultStreetValue,
        zipCode: action.payload.zipCode
          ? removeWhitespace(action.payload.zipCode)
          : defaultZipCodeValue,
        city: action.payload.city ?? defaultCityValue,
      };
    },
    setDataPointsShared: (
      state,
      action: PayloadAction<Partial<CardDataPointsShared>>
    ) => {
      state.dataPointsShared = {
        ...state.dataPointsShared,
        ...action.payload,
      };
    },
  },
});

export const {
  updateCurrentStep,
  goToNextStep,
  goToPreviousStep,
  setCurrentStepName,
  updateForm,
  setUserId,
  setClientId,
  setApiStatus,
  updateApplicationStatus,
  updateCurrentOrganizationIdentifier,
  clearCardApplicationState,
  removeClearedState,
  updateApplicantData,
  updateApplicantAddress,
  setDataPointsShared,
} = cardApplicationSlice.actions;

export default cardApplicationSlice.reducer;

listenerMiddleware.startListening({
  matcher: isAnyOf(updateCurrentStep, goToNextStep, goToPreviousStep),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(cardApplicationSlice.actions.setStepStartTimestamp());
  },
});
